'use strict';

/**
 * Estado de la PLP al que hay que volver tras pasar por la PDP: qué producto
 * hay que enfocar y cuántas páginas extra (de "ver más") hay que recargar
 * antes de poder encontrarlo en el grid.
 *
 * Ninguno de los dos viaja en la query string. La clave de la page cache de
 * SFCC es la URL completa, así que un `?product=<pid>&sz=<n>` distinto por
 * cada gafa y cada nivel de paginación crea una entrada de caché por
 * combinación y convierte la vuelta desde la PDP en un miss garantizado (y de
 * paso expulsa de la caché las entradas compartidas).
 *
 * Se guarda en el state del history, que el navegador restaura en back/forward
 * aunque el documento se vuelva a descargar del servidor. Como espejo se deja
 * en sessionStorage, por si algún `replaceState` posterior pisa el state: la
 * clave incluye la URL completa (path + query) y se borra al leerla, para que
 * un estado viejo no dispare una restauración inesperada en una visita nueva.
 */

// Variables
const stateKey = 'plpTargetProduct';
const pagesToRestoreStateKey = 'plpPagesToRestore';
const storageKeyPrefix = 'plpTargetProduct:';

// Functions

/**
 * Build the session storage key for a given PLP url
 * @param {string} url - Url including path and query string
 * @returns {string} - Session storage key
 */
const getStorageKey = (url) => `${storageKeyPrefix}${url}`;

/**
 * Store the target product and how many pages to restore, and rewrite the
 * current history entry
 * @param {string} productId - The product id the user clicked on
 * @param {number} pagesToRestore - Number of extra pages loaded before the click
 * @param {string} url - The url to write in the current history entry
 * @returns {void}
 */
const saveTargetProduct = (productId, pagesToRestore, url) => {
  const state = Object.assign({}, window.history.state);

  if (productId) {
    state[stateKey] = productId;
    state[pagesToRestoreStateKey] = pagesToRestore;
  }

  // La reescritura de la url se hace siempre: puede llevar el `srule` que el
  // servidor necesita para reproducir el listado, haya pid o no.
  window.history.replaceState(state, '', url);

  if (!productId) {
    return;
  }

  try {
    sessionStorage.setItem(
      getStorageKey(url),
      JSON.stringify({ productId, pagesToRestore })
    );
  } catch (error) {
    // Safari en privado y los navegadores con almacenamiento bloqueado lanzan
    // aquí. El state del history ya cubre el caso normal, así que se ignora.
  }
};

/**
 * Read the PLP restore state for the current page and consume it
 * @returns {Object} - { productId: string|null, pagesToRestore: number }
 */
const readPlpRestoreState = () => {
  const state = window.history.state || {};
  const url = `${window.location.pathname}${window.location.search}`;
  let productId = state[stateKey] || null;
  let pagesToRestore = productId ? state[pagesToRestoreStateKey] || 0 : 0;

  try {
    const storageKey = getStorageKey(url);
    const storedValue = sessionStorage.getItem(storageKey);

    sessionStorage.removeItem(storageKey);

    if (!productId && storedValue) {
      const stored = JSON.parse(storedValue);

      productId = stored.productId || null;
      pagesToRestore = stored.pagesToRestore || 0;
    }
  } catch (error) {
    // Ver saveTargetProduct
  }

  // Enlaces antiguos ya compartidos o indexados con `?product=`: no se sabe
  // cuántas páginas había cargadas, así que solo se intenta el scroll.
  if (!productId) {
    productId = new URLSearchParams(window.location.search).get('product');
    pagesToRestore = 0;
  }

  return { productId, pagesToRestore };
};

export { saveTargetProduct, readPlpRestoreState };
